import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errorText } from "@/lib/api";
import { youtubeId, isFileVideo } from "@/components/Hero";
import { resolveImage } from "@/lib/api";
import VideoUploadField from "./VideoUploadField";
import { TextField, PrimaryButton, GhostButton } from "./fields";

const EMPTY = { brand: "", brand_slug: "", video_url: "", poster: "", start: 0, line1: "", line2: "", line3: "", subtext: "", cta_label: "", cta_href: "" };

export default function HeroSlidesManager() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["hero-slides", "all"], queryFn: async () => (await api.get("/admin/hero-slides")).data });
  const [slides, setSlides] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setSlides(data.map((s) => ({ ...EMPTY, ...s }))); }, [data]);

  const set = (i, k) => (v) => setSlides((all) => all.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const remove = (i) => setSlides((all) => all.filter((_, j) => j !== i));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/hero-slides", { slides: slides.map((s) => ({ ...s, start: Number(s.start) || 0 })) });
      toast.success("Hero slides saved.");
      qc.invalidateQueries({ queryKey: ["hero-slides"] });
    } catch (err) {
      toast.error(errorText(err, "Could not save slides."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-hero-slides">
      <div className="mb-8 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.3em] text-mist" data-testid="admin-hero-count">{slides.length} homepage slides · auto-advance every 14 s</p>
        <div className="flex gap-3">
          {slides.length < 8 && <GhostButton onClick={() => setSlides([...slides, { ...EMPTY }])} data-testid="admin-hero-add" className="inline-flex items-center gap-2"><Plus size={12} /> Add slide</GhostButton>}
          <PrimaryButton onClick={save} disabled={busy} data-testid="admin-hero-save">{busy ? "Saving…" : "Save slides"}</PrimaryButton>
        </div>
      </div>
      <div className="space-y-6">
        {slides.map((s, i) => (
          <div key={i} className="grid gap-4 border border-navy-ink/10 bg-white/40 p-5 lg:grid-cols-12" data-testid={`admin-hero-slide-${i}`}>
            <div className="lg:col-span-4">
              <div className="aspect-video w-full overflow-hidden border border-navy-ink/10 bg-navy-ink/5">
                {isFileVideo(s.video_url) ? <video src={resolveImage(s.video_url)} muted loop autoPlay playsInline className="h-full w-full object-cover" data-testid={`admin-hero-slide-${i}-thumb`} />
                  : youtubeId(s.video_url) ? <img src={`https://img.youtube.com/vi/${youtubeId(s.video_url)}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" data-testid={`admin-hero-slide-${i}-thumb`} />
                  : s.poster ? <img src={resolveImage(s.poster)} alt="" className="h-full w-full object-cover" data-testid={`admin-hero-slide-${i}-thumb`} />
                  : <p className="p-4 text-xs text-red-700">Paste an MP4/WebM link, upload a clip, paste a YouTube link, or set a poster image for a still slide</p>}
              </div>
              <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-mist">Slide {String(i + 1).padStart(2, "0")}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
              <div className="sm:col-span-2"><VideoUploadField value={s.video_url} onChange={set(i, "video_url")} testId={`admin-hero-slide-${i}-video`} /></div>
              <TextField label="Poster image URL (optional, shown while loading)" value={s.poster || ""} onChange={set(i, "poster")} testId={`admin-hero-slide-${i}-poster`} placeholder="/video/sunseeker-poster.jpg" />
              <TextField label="YouTube start at (seconds)" type="number" value={s.start} onChange={set(i, "start")} testId={`admin-hero-slide-${i}-start`} />
              <TextField label="Brand label" value={s.brand} onChange={set(i, "brand")} testId={`admin-hero-slide-${i}-brand`} />
              <TextField label="Brand slug (for test ids)" value={s.brand_slug} onChange={set(i, "brand_slug")} testId={`admin-hero-slide-${i}-slug`} placeholder="sunseeker" />
              <TextField label="Headline line 1" value={s.line1} onChange={set(i, "line1")} testId={`admin-hero-slide-${i}-line1`} />
              <TextField label="Headline line 2 (italic gold)" value={s.line2} onChange={set(i, "line2")} testId={`admin-hero-slide-${i}-line2`} />
              <TextField label="Headline line 3" value={s.line3} onChange={set(i, "line3")} testId={`admin-hero-slide-${i}-line3`} />
              <TextField label="Button label" value={s.cta_label} onChange={set(i, "cta_label")} testId={`admin-hero-slide-${i}-cta`} placeholder="Explore Sunseeker" />
              <TextField label="Button link" value={s.cta_href} onChange={set(i, "cta_href")} testId={`admin-hero-slide-${i}-href`} placeholder="/yachts/sunseeker or #contact" />
              <div className="sm:col-span-2"><TextField label="Subtext" multiline value={s.subtext} onChange={set(i, "subtext")} testId={`admin-hero-slide-${i}-subtext`} /></div>
              <div className="sm:col-span-2 flex justify-end"><GhostButton onClick={() => remove(i)} data-testid={`admin-hero-slide-${i}-remove`} className="inline-flex items-center gap-2 hover:border-red-700 hover:text-red-700"><Trash2 size={12} /> Remove slide</GhostButton></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
